import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { ModalNames } from '|/app/modals';
import { useBrand } from '|/brand';
import { PersonaClient, ResumableInquiry } from '|/components/persona/types';
import { createPersonaClient, PersonaOptions } from '|/components/persona/utils/persona-client';
import BackstopContext from '|/context/backstop-context';
import { useNavigationBlockContext } from '|/context/navigation-block-context';
import UserInitializationContext from '|/context/user-initialization-context';
import useBrowserBackClose from '|/hooks/use-browser-back-close';
import useRumAction from '|/hooks/use-rum-action';

// Step-scoped: only known by whichever step (buyer-identity/co-signer-identity) is currently
// mounted, so it can't be a provider prop like `dl-reconditioning-context.tsx`'s. `usePersonaLifecycle`
// registers the current values every render; the SDK client's own callbacks — wired once at
// creation and potentially outliving several remounts of the step that created them — always read
// back through this registry rather than closing over the values directly, so they call the
// currently-mounted step even when that isn't who created the client.
export interface PersonaLifecycleCallbacks {
  sendEvent: (name: string, success: string, metadata: unknown) => void;
  handlePersonaComplete: (
    inquiryId: string,
    isCobuyer: boolean,
    templateId: string
  ) => Promise<void>;
  // isPreloadPhase is the value at the moment of error — swallow (no fallback modal) when true,
  // surface when false.
  onError: (isCobuyer: boolean, isPreloadPhase: boolean) => void;
  onReady: (isCobuyer: boolean) => void;
  onCancel: (isCobuyer: boolean) => void;
  // createIfAbsent's return value is synchronous and doesn't know the construction outcome yet, so
  // the caller must defer pp_persona_create until this notification arrives instead of firing it
  // optimistically off the synchronous return.
  onCreated: (
    isCobuyer: boolean,
    succeeded: boolean,
    explicitTrigger?: PersonaCreationTrigger,
    inquirySource?: PersonaInquirySource
  ) => void;
}

const noopPersonaLifecycleCallbacks: PersonaLifecycleCallbacks = {
  sendEvent: () => undefined,
  handlePersonaComplete: () => Promise.resolve(),
  onError: () => undefined,
  onReady: () => undefined,
  onCancel: () => undefined,
  onCreated: () => undefined
};

// Told apart by the caller so it can fire pp_persona_create vs the new pp_persona_client_reused —
// this function only ever performs at most one of the two.
// 'existing' means a client is genuinely already there (ready or still connecting) — a true
// reuse. 'pending' means a construction is merely in flight with no client yet (a second caller
// arrived while the first is still awaiting createPersonaClient) — nothing exists to reuse; the
// caller should treat this the same as 'existing' for control flow (don't start a new attempt)
// but must not report it as a reuse, since createIfAbsent's original two-state guard reported both
// identically and inflated pp_persona_client_reused.
export type PersonaCreationOutcome = 'created' | 'existing' | 'pending';

// The full set of reasons createIfAbsent can be called, threaded through as explicitTrigger so
// pp_persona_create's attribution survives a fold remount that happens while construction is
// still in flight — a hook-local ref (the alternative) doesn't survive that remount.
// 'eager-preload' is PersonaEagerPreload's page-level construction, ahead of any step.
export type PersonaCreationTrigger =
  | 'preload'
  | 'retry'
  | 'cancel'
  | 'reset'
  | 'auth-recovery'
  | 'eager-preload';

// Whether this construction is resuming a previously-cancelled inquiry or starting a fresh one.
export type PersonaInquirySource = 'new' | 'resumed';

// Two per-role fallback objects — seeded as callbacksRef's initial value below — so a client
// constructed before any step has registered (the eager-preload path) still reports a
// *successful* pp_persona_create instead of silently losing it to noopPersonaLifecycleCallbacks.
// onError/onReady/onCancel/handlePersonaComplete stay no-ops deliberately: no UI is waiting on an
// eager construction, so there's nothing for those to do until a real step registers. Both
// fallbacks' sendEvent resolves sendEventPropRef live rather than closing over the injected prop
// directly (see PersonaLifecycleProvider), so it never freezes a stale trackEvent.
const createFallbackPersonaLifecycleCallbacks = (
  role: 'buyer' | 'cobuyer',
  sendEventPropRef: React.MutableRefObject<PersonaLifecycleCallbacks['sendEvent'] | undefined>
): PersonaLifecycleCallbacks => {
  const sendEvent: PersonaLifecycleCallbacks['sendEvent'] = (name, success, metadata) => {
    sendEventPropRef.current?.(name, success, metadata);
  };
  return {
    ...noopPersonaLifecycleCallbacks,
    sendEvent,
    onCreated: (isCobuyer, succeeded, explicitTrigger, inquirySource) => {
      if (!succeeded || explicitTrigger === undefined) return;
      sendEvent('pp_persona_create', 'True', {
        isCosigner: isCobuyer,
        trigger: explicitTrigger,
        preloadSurface: role,
        inquirySource
      });
    }
  };
};

// 'blocked-on-auth' is a distinct state (not folded into 'idle') so the userId-arrives-late
// effect below knows exactly which roles to retry without re-deriving that fact from side
// channels.
type PersonaRoleTag = 'idle' | 'blocked-on-auth' | 'creating' | 'ready';

// Persona caps sessions at 25 per inquiry. Whether a resumed construction reattaches to the
// existing session or mints a new one under the hood isn't confirmed by Persona's public docs —
// capping resumes here keeps that unconfirmed inference from ever walking this role toward that
// ceiling if the inference turns out to be wrong.
const MAX_PERSONA_RESUME_ATTEMPTS = 3;

interface PersonaRoleState {
  tag: PersonaRoleTag;
  // Bumped by destroy() so an attempt abandoned mid-flight (e.g. retry() while its predecessor's
  // createPersonaClient call is still pending) can tell it's been superseded and must not mutate
  // shared state or fire callbacks on a newer attempt's behalf.
  generation: number;
  // "Has the user ever clicked Upload for this role, this session." Must survive fold remounts —
  // a hook-local copy would reset to true on every collapse/reopen, silently re-swallowing an
  // error that should now surface, for a customer who already clicked once in a prior visit to
  // this fold. reset() re-arms it, since a reset means "treat this role as never having engaged."
  isPreloadPhase: boolean;
  // The most recently cancelled inquiry for this role, held for the next construction to resume.
  // Modeled on isPreloadPhase: declared here, seeded null, never mentioned in destroy() — that's
  // what lets it survive every teardown path. Cleared by createIfAbsent's consume (on attempt),
  // reset(), and disableRole().
  resumableInquiry: ResumableInquiry | null;
  // How many times resumableInquiry has been consumed (i.e. actually resumed) for the current
  // inquiry chain. Same survive-destroy() lifetime as resumableInquiry. Zeroed by reset() and
  // disableRole(), and re-armed by the SDK onCancel handler whenever that cancel doesn't stash
  // (cap reached, or blank identifiers) — the cap bounds resumes per inquiry, not per role for the
  // page's lifetime, so a new inquiry chain reached via such a cancel starts at 0. Not re-armed
  // when the new inquiry instead comes from an abandoned or failed resume (no cancel fires in
  // either case) — that inquiry inherits whatever count the superseded/failed attempt already
  // spent, which is intentional: see createIfAbsent's consume-on-attempt comment.
  resumeAttemptCount: number;
}

const createInitialRoleState = (): PersonaRoleState => ({
  tag: 'idle',
  generation: 0,
  isPreloadPhase: true,
  resumableInquiry: null,
  resumeAttemptCount: 0
});

interface PersonaLifecycleContextValue {
  registerCallbacks: (isCobuyer: boolean, callbacks: PersonaLifecycleCallbacks) => void;
  createIfAbsent: (
    isCobuyer: boolean,
    explicitTrigger?: PersonaCreationTrigger
  ) => PersonaCreationOutcome;
  open: (isCobuyer: boolean) => boolean;
  retry: (isCobuyer: boolean, explicitTrigger?: PersonaCreationTrigger) => PersonaCreationOutcome;
  reset: (isCobuyer: boolean) => void;
  markClicked: (isCobuyer: boolean) => void;
  getIsPersonaReady: (isCobuyer: boolean) => boolean;
  // Lets a consumer's deferred unmount diagnostic (fold collapse vs. genuine checkout exit) tell
  // whether the provider itself is still mounted, one tick after its own cleanup — see
  // usePersonaLifecycle.ts's fold-collapse-during-scan RUM diagnostic.
  getIsProviderAlive: () => boolean;
  // Increments each time the provider tears its clients down (in dev, StrictMode's simulated
  // remount). A consumer that does something once per provider lifetime keys its latch on this, so
  // the work re-arms after a teardown instead of being lost to it — see PersonaEagerPreload.
  getProviderEpoch: () => number;
  // A consumer whose own `enabled` flips true→false mid-session (e.g. the DL-reconditioning gate
  // re-deciding) has no other way to force teardown — destroy() itself stays provider-internal.
  // Also closes this role's scan modal if it's the one currently open, so a disabled role never
  // leaves the customer on a modal backed by a dead client.
  disableRole: (isCobuyer: boolean) => void;
  isPersonaScanOpen: boolean;
  isCobuyerPersona: boolean;
}

// Missing-provider default: silent no-op, never throws — matches backstop-context.tsx /
// dl-reconditioning-context.tsx. Exported so a consumer (usePersonaLifecycle) can detect this
// sentinel by a plain identity comparison and fire its own one-time RUM action, rather than
// reaching into Context._currentValue (an undocumented React internal).
export const defaultPersonaLifecycleContextValue: PersonaLifecycleContextValue = {
  registerCallbacks: () => undefined,
  createIfAbsent: () => 'existing',
  open: () => false,
  retry: () => 'existing',
  reset: () => undefined,
  markClicked: () => undefined,
  getIsPersonaReady: () => false,
  getIsProviderAlive: () => false,
  getProviderEpoch: () => 0,
  disableRole: () => undefined,
  isPersonaScanOpen: false,
  isCobuyerPersona: false
};

const PersonaLifecycleContext = createContext<PersonaLifecycleContextValue>(
  defaultPersonaLifecycleContextValue
);

const PersonaLifecycleProvider: React.FC<
  React.PropsWithChildren<{
    // Page-level sendEvent for the fallback callbacks below. Required-but-nullable (not optional),
    // matching DlReconditioningProvider's isCheckItOutTreatment: every mount site must say explicitly
    // whether it has one, so a future second mount site can't silently drop eager-preload telemetry.
    // The provider must not resolve this itself — useAnalyticsContext throws outside an
    // AnalyticsProvider, and this needs to be renderable in the existing provider test suite.
    sendEvent: PersonaLifecycleCallbacks['sendEvent'] | undefined;
  }>
> = ({ sendEvent, children }) => {
  const { accessToken } = useContext(UserInitializationContext);
  const userId = accessToken?.user.user_id;
  const { isBackstopTreatment } = useContext(BackstopContext);
  const { addOpenModal, removeOpenModal } = useNavigationBlockContext();
  const { brand } = useBrand();
  const { trackAction } = useRumAction();

  const personaClientRef = useRef<PersonaClient | null>(null);
  const personaClientCobuyerRef = useRef<PersonaClient | null>(null);
  const personaTimeoutCleanupRef = useRef<(() => void) | null>(null);
  const personaTimeoutCleanupCobuyerRef = useRef<(() => void) | null>(null);
  const roleStateRef = useRef<{ buyer: PersonaRoleState; cobuyer: PersonaRoleState }>({
    buyer: createInitialRoleState(),
    cobuyer: createInitialRoleState()
  });

  const getRoleState = useCallback(
    (isCobuyer: boolean) => roleStateRef.current[isCobuyer ? 'cobuyer' : 'buyer'],
    []
  );

  // Flips false only when the provider itself unmounts. React runs child effect cleanups before
  // parent ones, so a consumer's own unmount cleanup can't tell a fold collapse (this provider
  // survives) from a genuine checkout exit (this unmounts too) by reading this synchronously —
  // it has to defer via setTimeout(0) until this cleanup has also had a chance to run.
  const isAliveRef = useRef<boolean>(true);
  useEffect(() => {
    isAliveRef.current = true;
    return () => {
      isAliveRef.current = false;
    };
  }, []);
  const getIsProviderAlive = useCallback(() => isAliveRef.current, []);
  const providerEpochRef = useRef(0);
  const getProviderEpoch = useCallback(() => providerEpochRef.current, []);

  // Provider-level, shared by both roles: only one of the two SDK scan modals can ever be open at
  // once, so this is not a per-role concern.
  const [isCobuyerPersona, setIsCobuyerPersona] = useState<boolean>(false);
  // A construction's SDK callbacks (defined inside createIfAbsent's own closure) can resolve long
  // after the render that started them, by which point isCobuyerPersona may have moved on to the
  // other role — reading the ref instead of the closed-over state keeps that check live rather
  // than frozen at attempt-start.
  const isCobuyerPersonaRef = useRef<boolean>(false);
  const [isPersonaScanOpen, setIsPersonaScanOpen] = useState<boolean>(false);

  // Read live every render (not just captured at seed time) so the fallback callbacks below
  // always reach whoever is currently injected. Assigned here, during render, rather than in an
  // effect: PersonaEagerPreload is a child of this provider, and React runs a commit's child
  // effects before its parent's, so an effect-based update here could still be one render behind
  // a child's own effect-triggered createIfAbsent call on any commit after mount.
  const sendEventPropRef = useRef(sendEvent);
  // eslint-disable-next-line react-compiler/react-compiler
  sendEventPropRef.current = sendEvent;

  const callbacksRef = useRef<{
    buyer: PersonaLifecycleCallbacks;
    cobuyer: PersonaLifecycleCallbacks;
  }>({
    // eslint-disable-next-line react-compiler/react-compiler
    buyer: createFallbackPersonaLifecycleCallbacks('buyer', sendEventPropRef),
    // eslint-disable-next-line react-compiler/react-compiler
    cobuyer: createFallbackPersonaLifecycleCallbacks('cobuyer', sendEventPropRef)
  });

  const registerCallbacks = useCallback(
    (isCobuyer: boolean, callbacks: PersonaLifecycleCallbacks) => {
      callbacksRef.current[isCobuyer ? 'cobuyer' : 'buyer'] = callbacks;
    },
    []
  );

  const callbacksFor = useCallback(
    (isCobuyer: boolean) => callbacksRef.current[isCobuyer ? 'cobuyer' : 'buyer'],
    []
  );

  const safeDestroyPersonaClient = useCallback((client: PersonaClient | null) => {
    if (!client) return;
    try {
      client.destroy();
    } catch (error) {
      console.warn('Persona client destroy warning:', error);
    }
  }, []);

  const clearPersonaTimeout = useCallback((isCobuyer: boolean) => {
    const ref = isCobuyer ? personaTimeoutCleanupCobuyerRef : personaTimeoutCleanupRef;
    if (ref.current) {
      ref.current();
      ref.current = null;
    }
  }, []);

  const getIsPersonaReady = useCallback(
    (isCobuyer: boolean): boolean => getRoleState(isCobuyer).tag === 'ready',
    [getRoleState]
  );

  // Provider-internal only — not on PersonaLifecycleContextValue. Wrapped by retry(), reset(),
  // closePersona(), and disableRole() below; usePersonaLifecycle only ever reaches it through one
  // of those, never directly.
  const destroy = useCallback(
    (isCobuyer: boolean) => {
      clearPersonaTimeout(isCobuyer);
      const state = getRoleState(isCobuyer);
      // Also abandons any construction still in flight for this role — without this, retry()
      // (destroy then createIfAbsent) would leave the role 'creating' from an earlier attempt,
      // so createIfAbsent's idempotency guard would return 'existing'/'pending' and arm nothing
      // new, stranding the customer with no path out. The generation bump is what stops that
      // abandoned attempt from mutating state once it does resolve.
      state.generation += 1;
      state.tag = 'idle';
      if (isCobuyer) {
        safeDestroyPersonaClient(personaClientCobuyerRef.current);
        personaClientCobuyerRef.current = null;
      } else {
        safeDestroyPersonaClient(personaClientRef.current);
        personaClientRef.current = null;
      }
    },
    [clearPersonaTimeout, getRoleState, safeDestroyPersonaClient]
  );

  // Fires only when the provider itself unmounts (genuine checkout exit) — never on a consumer
  // hook's unmount, since the provider lives above every fold, not inside one. destroy() bumps the
  // generation, so an in-flight construction is torn down when it resolves, and leaves the role
  // idle, so StrictMode's remount doesn't find it stuck at 'creating'.
  useEffect(() => {
    return () => {
      providerEpochRef.current += 1;
      destroy(false);
      destroy(true);
    };
  }, [destroy]);

  const createIfAbsent = useCallback(
    (isCobuyer: boolean, explicitTrigger?: PersonaCreationTrigger): PersonaCreationOutcome => {
      const clientRef = isCobuyer ? personaClientCobuyerRef : personaClientRef;
      const timeoutCleanupRef = isCobuyer
        ? personaTimeoutCleanupCobuyerRef
        : personaTimeoutCleanupRef;
      const roleKey = isCobuyer ? 'cobuyer' : 'buyer';
      const state = roleStateRef.current[roleKey];

      if (clientRef.current !== null) {
        return 'existing';
      }
      if (state.tag === 'creating') {
        return 'pending';
      }

      state.tag = 'creating';
      clearPersonaTimeout(isCobuyer);

      // Captured now so every callback below can tell whether a later destroy() (a retry() or
      // reset() while this construction is still in flight) has superseded this attempt.
      const attemptGeneration = state.generation;
      const isCurrentAttempt = () => roleStateRef.current[roleKey].generation === attemptGeneration;

      let errorHandled = false;
      // Decided partway through the async construction below (consumed a resume stash or built
      // fresh) but read by the watchdog's setTimeout callback, which is declared before that async
      // block runs — same closure shape as errorHandled just above, for the same reason. undefined
      // (not null) is the not-yet-decided state: the onCreated parameter this eventually feeds is a
      // forced-optional `inquirySource?: PersonaInquirySource`, and null isn't assignable there
      // under this repo's strict config.
      let inquirySource: PersonaInquirySource | undefined;

      const personaTimeout = setTimeout(() => {
        if (!isCurrentAttempt()) return;
        // trigger records where this attempt came from (eager-preload vs a step's own preload/retry),
        // not whether the customer engaged with it — a customer can click Upload while an
        // eager-preload construction is still in flight, which flips isPreloadPhase to false without
        // changing trigger. A pre-ramp check isolating engagement-free timeouts must key off
        // isPreloadPhase, not trigger alone — see TXCC-9790's pre-ramp inspection note.
        trackAction('persona_timeout', {
          isCobuyer,
          trigger: explicitTrigger,
          isPreloadPhase: state.isPreloadPhase,
          inquirySource
        });
        handleError();
      }, 15000);
      const clearThisTimeout = () => {
        clearTimeout(personaTimeout);
      };
      timeoutCleanupRef.current = clearThisTimeout;

      const handleError = () => {
        if (!isCurrentAttempt() || errorHandled) return;
        errorHandled = true;
        clearThisTimeout();
        // Routes through destroy() rather than a bare `state.tag = 'idle'` — a client can already
        // be constructed and assigned by the time a later SDK error fires (e.g. a post-ready
        // connection drop), and without this, that now-broken client stays in clientRef forever:
        // createIfAbsent's `clientRef.current !== null` guard would report it 'existing' and never
        // rebuild. destroy() also bumps generation, so a construction that resolves after this
        // error is recognized as superseded and torn down instead of clobbering the next attempt.
        destroy(isCobuyer);
        // Scoped to this role's own scan — isPersonaScanOpen is shared provider state, so a
        // background failure on one role must never clear the other role's legitimately open scan.
        // Reads the ref, not the isCobuyerPersona closed over at attempt-start: this callback can
        // resolve long after the other role has since opened its own scan, and the stale value
        // would otherwise match and incorrectly dismiss that live scan.
        if (isCobuyerPersonaRef.current === isCobuyer) {
          setIsPersonaScanOpen(false);
        }
        callbacksFor(isCobuyer).onError(isCobuyer, state.isPreloadPhase);
      };

      const options: PersonaOptions = {
        onCancel: (response, templateId) => {
          if (!isCurrentAttempt()) return;
          setIsPersonaScanOpen(false);
          callbacksFor(isCobuyer).sendEvent('pp_persona_cancel', 'True', {
            isCosigner: isCobuyer,
            response,
            templateId
          });
          // Stash the cancelled inquiry for the next construction to resume, before destroy()
          // below clears everything else. Only when both identifiers are genuinely present — a
          // partial/blank response paired with an omitted templateId would trip the SDK's "Either
          // templateId... or inquiryId must be specified" throw — and only while this role hasn't
          // already spent this inquiry's resume budget.
          if (
            response.inquiryId &&
            response.sessionToken &&
            state.resumeAttemptCount < MAX_PERSONA_RESUME_ATTEMPTS
          ) {
            state.resumableInquiry = {
              inquiryId: response.inquiryId,
              sessionToken: response.sessionToken
            };
          } else {
            // Nothing gets stashed, so the next construction is necessarily a new inquiry — see
            // the resumeAttemptCount field comment for why this re-arm is per-inquiry, not
            // per-role. Safe unconditionally: an abandoned in-flight resume superseded by retry()
            // is stopped by the isCurrentAttempt() guard at the top of this handler, before ever
            // reaching this branch.
            state.resumeAttemptCount = 0;
          }
          // The provider only destroys here — it does NOT call createIfAbsent itself, so that the
          // resulting pp_persona_create/pp_persona_client_reused telemetry stays owned by whoever
          // calls createIfAbsent, the same as every other creation site. Deferred via
          // setTimeout(0) in usePersonaLifecycle's handleCancel to avoid re-entering the SDK
          // synchronously from within its own onCancel callback.
          destroy(isCobuyer);
          callbacksFor(isCobuyer).onCancel(isCobuyer);
        },
        onComplete: (inquiryId, templateId) => {
          if (!isCurrentAttempt()) return;
          clearThisTimeout();
          setIsPersonaScanOpen(false);
          const { handlePersonaComplete } = callbacksFor(isCobuyer);
          void handlePersonaComplete(inquiryId, isCobuyer, templateId)
            .then(() => {
              // Checked again here, not just at the top of onComplete — a retry()/reset() can
              // supersede this attempt during the awaited call. destroy() is the single place that
              // keeps client/tag/generation moving together atomically; hand-rolling any one piece
              // of that here is exactly how this file's last few bugs got in.
              if (isCurrentAttempt()) {
                destroy(isCobuyer);
              }
            })
            .catch(() => {
              // No isCurrentAttempt() guard needed here — handleError() already starts with one,
              // so a stale attempt (already superseded by a later retry/reset) correctly no-ops
              // instead of setting error state or popping the fallback modal on that newer
              // attempt's behalf.
              handleError();
            });
        },
        onLoad: (timeElapsed, templateId) => {
          if (isCurrentAttempt() && !errorHandled) {
            callbacksFor(isCobuyer).sendEvent('pp_persona_load', 'True', {
              isCosigner: isCobuyer,
              timeElapsed,
              templateId
            });
          }
        },
        onError: templateId => {
          if (!isCurrentAttempt()) return;
          handleError();
          callbacksFor(isCobuyer).sendEvent('pp_persona_error', 'False', {
            isCosigner: isCobuyer,
            name: 'connection error modal',
            message: 'there is a problem connecting to persona',
            templateId
          });
        },
        onReady: (timeElapsed, templateId) => {
          if (isCurrentAttempt() && !errorHandled) {
            clearThisTimeout();
            state.tag = 'ready';
            callbacksFor(isCobuyer).onReady(isCobuyer);
            callbacksFor(isCobuyer).sendEvent('pp_persona_ready', 'True', {
              isCosigner: isCobuyer,
              timeElapsed,
              templateId
            });
          }
        },
        onEvent: () => {
          /* no-op */
        }
      };

      void (async () => {
        try {
          if (userId === undefined) {
            // Clear the watchdog armed above — without this, an orphaned 15s timer fires
            // persona_timeout + handleError for a customer whose only problem was a missing
            // access token, potentially popping the fallback modal well after this call returned.
            clearThisTimeout();
            if (!isCurrentAttempt()) return;
            // Bumped so the auth-recovery effect's own createIfAbsent call (a genuinely new
            // attempt) never shares this attempt's generation — harmless today only because no
            // client/timer survives this branch, not because generation would otherwise be unique.
            state.generation += 1;
            // Distinct from 'idle' — the auth-recovery effect below watches for this tag and
            // retries automatically once userId becomes available, without the customer having
            // to do anything (and without the consumer's preload effect needing to know why).
            state.tag = 'blocked-on-auth';
            callbacksFor(isCobuyer).sendEvent('pp_persona_resolution', 'False', {
              isCosigner: isCobuyer,
              outcome: 'no-userId'
            });
            callbacksFor(isCobuyer).onCreated(isCobuyer, false, explicitTrigger, inquirySource);
            // Reaching here from retryPersona (isPreloadPhase already false — markClicked ran
            // before retry()) would otherwise leave the connecting modal open with no way to
            // close it: no watchdog is armed for this branch, and this modal has no close icon.
            // onError's own isPreloadPhase guard keeps the passive preload case silent, unchanged.
            // Calls onError directly rather than through handleError() — errorHandled/setIsPersonaScanOpen
            // don't apply here (no scan was ever open, no client was ever created for this attempt).
            callbacksFor(isCobuyer).onError(isCobuyer, state.isPreloadPhase);
            return;
          }
          // Consume (read, then clear) the stash here — after the no-userId guard above, not
          // before it. Clearing on attempt rather than on success: onError in persona-client.ts
          // discards the SDK error, so a resume failure is undetectable here, and leaving the
          // token in place for a retry would let an expired token be retried forever (error →
          // fallback modal → Retry → same expired token → error).
          const resumableInquiry = state.resumableInquiry;
          state.resumableInquiry = null;
          inquirySource = resumableInquiry ? 'resumed' : 'new';
          if (inquirySource === 'resumed') {
            state.resumeAttemptCount += 1;
          }
          const client = await createPersonaClient(
            isCobuyer,
            // Indirection, not callbacksFor(isCobuyer).sendEvent by value: this closure is wired
            // into the SDK client's onEvent/error handlers for the client's entire lifetime
            // (persona-client.ts), so a by-value capture would freeze pp_persona_event/
            // pp_persona_create_failure attribution to whoever was registered at construction —
            // stale the moment a real step registers after an eager-preload construction.
            (name, success, metadata) => {
              callbacksFor(isCobuyer).sendEvent(name, success, metadata);
            },
            options,
            userId,
            brand,
            resumableInquiry
          );
          if (!isCurrentAttempt()) {
            // A later destroy() (retry/reset) superseded this attempt while the SDK call was in
            // flight — tear down the orphaned client instead of letting it clobber the current
            // attempt's ref or fire callbacks on its behalf.
            safeDestroyPersonaClient(client);
            return;
          }
          if (client !== null) {
            if (isCobuyer) {
              personaClientCobuyerRef.current = client;
            } else {
              personaClientRef.current = client;
            }
            callbacksFor(isCobuyer).onCreated(isCobuyer, true, explicitTrigger, inquirySource);
          } else {
            handleError();
            callbacksFor(isCobuyer).onCreated(isCobuyer, false, explicitTrigger, inquirySource);
          }
        } catch (error) {
          if (!isCurrentAttempt()) return;
          handleError();
          callbacksFor(isCobuyer).onCreated(isCobuyer, false, explicitTrigger, inquirySource);
        }
      })();

      return 'created';
    },
    [
      brand,
      userId,
      callbacksFor,
      clearPersonaTimeout,
      destroy,
      safeDestroyPersonaClient,
      trackAction
    ]
  );

  // Call once, unconditionally, at the top of a user-initiated click handler: the preload window
  // is over the moment the customer actively requests Persona, regardless of whether a client
  // happens to be ready yet.
  const markClicked = useCallback(
    (isCobuyer: boolean) => {
      // Set unconditionally (not just on a not-yet-ready click) so a browser-back still knows
      // which role's client to destroy.
      setIsCobuyerPersona(isCobuyer);
      isCobuyerPersonaRef.current = isCobuyer;
      getRoleState(isCobuyer).isPreloadPhase = false;
    },
    [getRoleState]
  );

  const open = useCallback(
    (isCobuyer: boolean): boolean => {
      const clientRef = isCobuyer ? personaClientCobuyerRef : personaClientRef;
      if (!getIsPersonaReady(isCobuyer) || clientRef.current === null) {
        return false;
      }
      setIsCobuyerPersona(isCobuyer);
      isCobuyerPersonaRef.current = isCobuyer;
      clientRef.current.open();
      setIsPersonaScanOpen(true);
      return true;
    },
    [getIsPersonaReady]
  );

  const retry = useCallback(
    (isCobuyer: boolean, explicitTrigger?: PersonaCreationTrigger): PersonaCreationOutcome => {
      destroy(isCobuyer);
      return createIfAbsent(isCobuyer, explicitTrigger);
    },
    [destroy, createIfAbsent]
  );

  // Pure destroy — does not recreate. The caller owns scheduling the follow-up createIfAbsent, so
  // that attempt's telemetry stays attributed to whoever triggers it, matching onCancel above.
  const reset = useCallback(
    (isCobuyer: boolean) => {
      // Scoped to the caller's own role — the other role's client is a different step's concern
      // and may already be preloaded and ready.
      destroy(isCobuyer);
      const state = getRoleState(isCobuyer);
      state.isPreloadPhase = true;
      // "Treat this role as never having engaged" also means never having a resume to offer, and
      // a fully re-armed cap — cleared here and by disableRole(), outside of createIfAbsent's own
      // consume-on-attempt.
      state.resumableInquiry = null;
      state.resumeAttemptCount = 0;
    },
    [destroy, getRoleState]
  );

  const closePersona = useCallback(() => {
    // Routes through destroy() (not a hand-rolled teardown) so this also bumps the generation
    // counter — otherwise a still-in-flight construction for this role could resolve after this
    // close and write its client back into the ref as if nothing happened.
    destroy(isCobuyerPersona);
    setIsPersonaScanOpen(false);
  }, [isCobuyerPersona, destroy]);

  const disableRole = useCallback(
    (isCobuyer: boolean) => {
      if (isCobuyerPersona === isCobuyer) {
        setIsPersonaScanOpen(false);
      }
      destroy(isCobuyer);
      // Insurance against a re-enable landing on a stash from before the disable — matches the
      // same-purpose resets usePersonaLifecycle.ts's teardown effect already does for its own
      // hook-local refs (hasPreloadedRef, hasShownConnectingModalRef, resolutionAttributionRef) on
      // this identical transition, which that effect's own comment calls "believed structurally
      // impossible" but keeps defensively anyway since the fix is otherwise easy to regress.
      const state = getRoleState(isCobuyer);
      state.resumableInquiry = null;
      state.resumeAttemptCount = 0;
    },
    [isCobuyerPersona, destroy, getRoleState]
  );

  // userId can arrive after the preload effect already ran (e.g. the access token resolves after
  // this provider mounts) — recorded here, not in usePersonaLifecycle, so that hook's preload
  // effect doesn't need to change at all. A role stuck in 'blocked-on-auth' gets a fresh attempt
  // the moment userId becomes available. Passes its own trigger explicitly since this call
  // originates from the provider itself, bypassing the consumer's emitCreationEvent/
  // pendingCreationTriggerRef stash — without it, a successful recovery would report no trigger
  // and pp_persona_create would silently drop for this attempt.
  useEffect(() => {
    if (userId === undefined) return;
    (['buyer', 'cobuyer'] as const).forEach(roleKey => {
      if (roleStateRef.current[roleKey].tag === 'blocked-on-auth') {
        createIfAbsent(roleKey === 'cobuyer', 'auth-recovery');
      }
    });
  }, [userId, createIfAbsent]);

  // Browser Back dismisses the Persona scan modal under pp-backstop treatment (destroy-and-reset —
  // Persona exposes no graceful close, so re-opening relaunches).
  useBrowserBackClose(ModalNames.Persona, isPersonaScanOpen && isBackstopTreatment, closePersona);

  // Register presence so pp_browser_nav_button's modalOpen/modalName reflect the Persona scan modal.
  useEffect(() => {
    if (!isPersonaScanOpen) return undefined;
    addOpenModal(ModalNames.Persona);
    return () => {
      removeOpenModal(ModalNames.Persona);
    };
  }, [isPersonaScanOpen, addOpenModal, removeOpenModal]);

  const value = useMemo<PersonaLifecycleContextValue>(
    () => ({
      registerCallbacks,
      createIfAbsent,
      open,
      retry,
      reset,
      markClicked,
      getIsPersonaReady,
      getIsProviderAlive,
      getProviderEpoch,
      disableRole,
      isPersonaScanOpen,
      isCobuyerPersona
    }),
    [
      registerCallbacks,
      createIfAbsent,
      open,
      retry,
      reset,
      markClicked,
      getIsPersonaReady,
      getIsProviderAlive,
      getProviderEpoch,
      disableRole,
      isPersonaScanOpen,
      isCobuyerPersona
    ]
  );

  return (
    <PersonaLifecycleContext.Provider value={value}>{children}</PersonaLifecycleContext.Provider>
  );
};

export default PersonaLifecycleContext;
export { PersonaLifecycleProvider };
